import { useMemo } from 'react';
import { usePermissionsQuery } from '../../../../hooks/api';
import type { AdvertiserOption } from '../../../../lib/api/permission';
import { DEFAULT_STALE_TIME } from '../../../../lib/queryClient';
import { useUserData } from './useUserData';

export const useAdvertisers = () => {
  const { userData } = useUserData(['embeddedUserId', 'acctId', 'isInternalUser']);

  const userID = Number(userData.embeddedUserId);
  const accountID = Number(userData.acctId);
  const {
    data: permissionsData,
    isLoading: isAdvertisersDataLoading,
    error: advertisersDataError,
  } = usePermissionsQuery(
    { user_id: userID, account_id: accountID },
    { enabled: !!userID && !!accountID, staleTime: 2 * DEFAULT_STALE_TIME },
  );

  const advertisers = useMemo(() => {
    const allAdvertisers = permissionsData?.advertisers || [];
    let advertiserOptions: AdvertiserOption[] = [];

    if (permissionsData?.buyerUser || userData.isInternalUser) {
      advertiserOptions = allAdvertisers.map(({ advId, advName }) => ({
        id: advId,
        value: advName,
      }));
    } else if (permissionsData?.hcp365User) {
      advertiserOptions = allAdvertisers
        .filter((a) => a.userAccess)
        .map(({ advId, advName }) => ({
          id: advId,
          value: advName,
        }));
    }

    return advertiserOptions;
  }, [permissionsData?.advertisers, permissionsData?.buyerUser, permissionsData?.hcp365User, userData.isInternalUser]);

  const accessAdvertiserIDs = useMemo(() => {
    if (permissionsData?.buyerUser || userData.isInternalUser) {
      return undefined;
    }

    return advertisers.map((a) => a.id);
  }, [permissionsData?.buyerUser, userData.isInternalUser, advertisers]);

  return {
    advertisers,
    accessAdvertiserIDs,
    isAdvertisersDataLoading,
    advertisersDataError,
  };
};
